type Options = Omit<RequestInit, 'body'> & { body?: string | Record<string, any> };

export let isServiceReady = false;
let onUnauthorized: () => void;

const handleUnauthorized = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  onUnauthorized();
};

let refreshPromise: Promise<boolean> | null = null;

const refreshToken = async (): Promise<boolean> => {
  // If a refresh is already in progress, wait for it instead of making a duplicate call
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const token = localStorage.getItem('refreshToken');

    if (!token) {
      return false;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_URL_API}/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ refreshToken: token }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

class Service {
  static init(callback: () => void) {
    onUnauthorized = callback;
    isServiceReady = true;
  }

  static async put<T>(url: string, body: Record<string, any>, options: Options = {}): Promise<T> {
    return await this.fetch(url, {
      ...options,
      method: 'PUT',
      body,
    });
  }

  static async post<T>(url: string, body: Record<string, any> = {}, options: Options = {}): Promise<T> {
    return await this.fetch(url, {
      ...options,
      method: 'POST',
      body,
    });
  }

  static async delete<T>(url: string, options: Options = {}): Promise<T> {
    return await this.fetch(url, {
      ...options,
      method: 'DELETE',
    });
  }

  static async get<T>(url: string, options: Options = {}): Promise<T> {
    return await this.fetch(url, {
      ...options,
      method: 'GET',
    });
  }

  static async patch<T>(url: string, body: Record<string, any>, options: Options = {}): Promise<T> {
    return await this.fetch(url, {
      ...options,
      method: 'PATCH',
      body,
    });
  }

  static async fetch<T>(url: string, options: Options = {}, ignoreUnauthorized?: boolean): Promise<T> {
    if (!isServiceReady) {
      throw new Error('Service not ready. Call Service.init() before using it.');
    }
    const updatedOptions = {
      ...options,
    };

    const token = localStorage.getItem('token');

    if (token) {
      updatedOptions.headers = {
        ...options?.headers,
        Authorization: `Bearer ${token}`,
      };
    }
    if (options.body) {
      updatedOptions.headers = {
        ...updatedOptions.headers,
        ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        'Accept': 'application/json',
      };
      if (typeof options.body !== 'string' && !(options.body instanceof FormData)) {
        updatedOptions.body = JSON.stringify(options.body);
      }
    }

    const response = await fetch(url, updatedOptions as RequestInit);

    if (!response.ok) {
      if (response.status === 401 && !ignoreUnauthorized) {
        const tokenRefreshed = await refreshToken();

        if (tokenRefreshed) {
          // Retry the original request with the new token (skip further refresh attempts)
          return this.fetch(url, options, true);
        }
        handleUnauthorized();
      }
      const errorBody = await response.json();
      errorBody.status = response.status;
      throw errorBody;
    }
    // 204 has no body: calling response.json() on it throws. Team invite
    // resend / member removal answer 204.
    if (response.status === 204) {
      return undefined as T;
    }
    return await response.json() as T;
  }
}

export default Service;
